import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useMemo, useRef, useState, type ChangeEvent } from "react";
import {
  User,
  Target,
  FileText,
  Mail,
  Phone,
  ClipboardList,
  Lock,
  Upload,
  Pencil,
  Trash2,
  Check,
  UserCircle,
  Loader2,
  ChevronRight,
  Eye,
  Download,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Label,
  Button,
  cn,
  toast,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  AvatarWithSkeleton,
} from "@cliently/ui";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { mergePortalDocumentDownloadConfig } from "../lib/portal-document-download";
import type { IntakeField } from "@cliently/types";
import {
  useCreatePortalDocument,
  useDeletePortalDocument,
  useUpdatePortalDocumentLabel,
} from "../hooks/use-documents";
import { useClientProfile } from "../hooks/use-client-profile";
import { resolveOrganizationMediaUrl } from "../lib/organization-media-url";
import { ALL_CLIENTS_KEY, useOrgStore } from "../stores/org.store";
import { LottieIllustration } from "../components/LottieIllustration";
import emptyBoxLottie from "../assets/lottie/empty-box.json";
import EmptyIllustration from "../assets/undraw/empty";
import { NoDataIllustration } from "../assets/undraw/NoDataIllustration";

const MAX_CLIENT_PROFILE_PHOTO_BYTES = 5 * 1024 * 1024;

const documentDisplayName = (doc: { name: string; label?: string | null }) => {
  const l = doc.label?.trim();
  return l && l.length > 0 ? l : doc.name;
}

const useClientObjectives = (activeClientId: string | null) => {
  const isAllProsMode = activeClientId === ALL_CLIENTS_KEY;

  return useQuery({
    queryKey: ["client-objectives", activeClientId],
    queryFn: async () => {
      const { data } = await api.get(
        isAllProsMode ? "/portal/aggregate/objectives" : "/portal/objectives",
      );
      return data as Array<{
        id: string;
        title: string;
        description: string | null;
        progress: number;
        doneAt: string | null;
        client?: { org?: { id: string; name: string } | null };
      }>;
    },
    retry: false,
  });
}

const useIntakeData = () => {
  return useQuery({
    queryKey: ["client-intake"],
    queryFn: async () => {
      const { data } = await api.get("/portal/intake");
      return data as {
        intakeForm: IntakeField[] | null;
        clientData: Record<string, string> | null;
        clientVersion: number;
      };
    },
    retry: false,
  });
}



export const ProfilePage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const activeClientId = useOrgStore((s) => s.activeClientId);
  const isAllProsMode = activeClientId === ALL_CLIENTS_KEY;
  const { data: profile } = useClientProfile();
  const photoInputRef = useRef<HTMLInputElement>(null);
  const updatePhotoMutation = useMutation({
    mutationFn: async (photo: string | null) => {
      const { data } = await api.patch("/portal/me", { photo });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["client-profile"] });
      toast.success(t("profile.photoSuccess"));
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { message?: string } } };
      toast.error(e?.response?.data?.message ?? t("profile.photoError"));
    },
  });

  const handleProfilePhotoChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (photoInputRef.current) photoInputRef.current.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t("profile.photoInvalidType"));
      return;
    }
    if (file.size > MAX_CLIENT_PROFILE_PHOTO_BYTES) {
      toast.error(t("profile.photoTooLarge", { max: "5 Mo" }));
      return;
    }
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
    await updatePhotoMutation.mutateAsync(base64);
  };

  const confirmRemoveProfilePhoto = async () => {
    await updatePhotoMutation.mutateAsync(null);
    setRemovePhotoDialogOpen(false);
  };

  const profilePhotoUrl = resolveOrganizationMediaUrl(profile?.photo);

  const { data: objectives } = useClientObjectives(activeClientId);
  const { data: intake } = useIntakeData();
  const uploadMutation = useCreatePortalDocument();
  const deleteMutation = useDeletePortalDocument();
  const updateLabelMutation = useUpdatePortalDocumentLabel();
  const fileRef = useRef<HTMLInputElement>(null);
  const [documentSearch, setDocumentSearch] = useState<string>("");
  const [documentTypeFilter, setDocumentTypeFilter] = useState<"all" | string>(
    "all",
  );
  const [editingDocId, setEditingDocId] = useState<string | null>(null);
  const [labelDraft, setLabelDraft] = useState<string>("");
  const [pendingUploadFile, setPendingUploadFile] = useState<File | null>(null);
  const [pendingUploadLabel, setPendingUploadLabel] = useState<string>("");
  const [removePhotoDialogOpen, setRemovePhotoDialogOpen] =
    useState<boolean>(false);
  const [deleteDocumentId, setDeleteDocumentId] = useState<string | null>(null);

  const hasIntakeData =
    intake?.clientData &&
    intake.intakeForm &&
    intake.intakeForm.length > 0 &&
    Object.keys(intake.clientData).length > 0;

  const downloadDocument = async (doc: {
    id: string;
    name: string;
    type: string;
    clientId?: string;
  }) => {
    const { data: blob } = await api.get(
      `/portal/documents/${doc.id}/download`,
      mergePortalDocumentDownloadConfig(isAllProsMode, doc.clientId, {
        responseType: "blob",
      }),
    );

    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = doc.name;
    a.click();
    URL.revokeObjectURL(blobUrl);
  };

  const handleUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPendingUploadFile(file);
    setPendingUploadLabel(file.name);

    // Reset so the same file can be selected again if needed.
    if (fileRef.current) fileRef.current.value = "";
  };

  const commitUpload = async () => {
    if (!pendingUploadFile) return;

    await uploadMutation.mutateAsync({
      file: pendingUploadFile,
      label: pendingUploadLabel.trim() || null,
    });

    setPendingUploadFile(null);
    setPendingUploadLabel("");
  };

  const cancelUpload = () => {
    setPendingUploadFile(null);
    setPendingUploadLabel("");

    if (fileRef.current) fileRef.current.value = "";
  };

  type ProfileDocument = {
    id: string;
    name: string;
    label?: string | null;
    url: string;
    type: string;
    createdAt: string;
    clientId?: string;
  };
  const documents = useMemo(
    () => ((profile?.documents ?? []) as ProfileDocument[]),
    [profile?.documents],
  );
  const documentTypes = useMemo(() => {
    return Array.from(new Set(documents.map((d: { type: string }) => d.type)));
  }, [documents]);

  const filteredDocuments = useMemo(() => {
    const normalizedSearch = documentSearch.trim().toLowerCase();
    return documents.filter((doc: ProfileDocument) => {
      const title = documentDisplayName(doc).toLowerCase();
      const labelPart = (doc.label ?? "").toLowerCase();
      const namePart = doc.name.toLowerCase();
      return (
        (documentTypeFilter === "all" || doc.type === documentTypeFilter) &&
        (!normalizedSearch ||
          title.includes(normalizedSearch) ||
          labelPart.includes(normalizedSearch) ||
          namePart.includes(normalizedSearch) ||
          doc.type.toLowerCase().includes(normalizedSearch))
      );
    });
  }, [documents, documentSearch, documentTypeFilter]);

  const previewDocument = async (doc: {
    id: string;
    name: string;
    type: string;
    clientId?: string;
  }) => {
    const { data: blob } = await api.get(
      `/portal/documents/${doc.id}/download`,
      mergePortalDocumentDownloadConfig(isAllProsMode, doc.clientId, {
        responseType: "blob",
      }),
    );
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          {t("profile.title")}
        </h1>
        <p className="text-muted-foreground">{t("profile.subtitle")}</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <User className="text-primary h-5 w-5" />
              <CardTitle>{t("profile.info")}</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col items-center gap-4 border-b pb-6 sm:flex-row sm:items-start">
              <AvatarWithSkeleton
                className="border-muted h-24 w-24 border-2"
                src={profilePhotoUrl}
                alt=""
                fallback={
                  <span className="bg-primary/10 text-primary text-2xl font-bold">
                    {profile?.firstName?.[0]}
                    {profile?.lastName?.[0]}
                  </span>
                }
              />
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => void handleProfilePhotoChange(e)}
                />
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={updatePhotoMutation.isPending}
                  >
                    {updatePhotoMutation.isPending ? (
                      <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <UserCircle className="mr-2 h-3.5 w-3.5" />
                    )}
                    {t("profile.changePhoto")}
                  </Button>
                  {profile?.photo ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="text-destructive"
                      disabled={updatePhotoMutation.isPending}
                      onClick={() => setRemovePhotoDialogOpen(true)}
                    >
                      <Trash2 className="mr-2 h-3.5 w-3.5" />
                      {t("profile.removePhoto")}
                    </Button>
                  ) : null}
                </div>
                <p className="text-muted-foreground text-xs">
                  {t("profile.photoHint", { max: "5 Mo" })}
                </p>
                <p className="text-muted-foreground text-xs">
                  {t(
                    "profile.photoSharedWithPractitioners",
                    "Cette photo est visible par tous vos praticiens.",
                  )}
                </p>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">
                  {t("profile.firstName")}
                </Label>
                <p className="font-medium">{profile?.firstName || "—"}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">
                  {t("profile.lastName")}
                </Label>
                <p className="font-medium">{profile?.lastName || "—"}</p>
              </div>
            </div>

            {profile?.email && (
              <div className="flex items-center gap-2 text-sm">
                <Mail className="text-muted-foreground h-4 w-4" />
                <span>{profile.email}</span>
              </div>
            )}

            {profile?.phone && (
              <div className="flex items-center gap-2 text-sm">
                <Phone className="text-muted-foreground h-4 w-4" />
                <span>{profile.phone}</span>
              </div>
            )}

            {profile?.specialty && (
              <div className="text-sm">
                <span className="text-muted-foreground">
                  {t("profile.specialty")} :{" "}
                </span>
                <span>{profile.specialty}</span>
              </div>
            )}

          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <FileText className="text-primary h-5 w-5" />
                <CardTitle>{t("profile.documents")}</CardTitle>
              </div>
              <div className="flex items-center gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,image/*"
                  onChange={handleUpload}
                  className="hidden"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploadMutation.isPending || !!pendingUploadFile}
                >
                  <Upload className="mr-2 h-3.5 w-3.5" />
                  {t("profile.uploadDocument")}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {pendingUploadFile && (
              <div className="bg-background space-y-2 rounded-lg border p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">
                      {pendingUploadFile.name}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      Renommez avant l'envoi
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    value={pendingUploadLabel}
                    onChange={(e) => setPendingUploadLabel(e.target.value)}
                    placeholder={pendingUploadFile.name}
                    className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring min-w-48 flex-1 rounded-md border px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2"
                    disabled={uploadMutation.isPending}
                    autoFocus
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => void commitUpload()}
                    disabled={uploadMutation.isPending}
                  >
                    <Check className="mr-2 h-4 w-4" />
                    {t("profile.sendDocument", "Envoyer")}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={cancelUpload}
                    disabled={uploadMutation.isPending}
                  >
                    {t("profile.cancel", "Annuler")}
                  </Button>
                </div>
              </div>
            )}

            {documents.length > 0 ? (
              <div className="space-y-2">
                <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
                  <input
                    type="text"
                    value={documentSearch}
                    onChange={(e) => setDocumentSearch(e.target.value)}
                    placeholder={t(
                      "profile.searchDocument",
                      "Rechercher un document",
                    )}
                    className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring h-9 rounded-md border px-3 text-sm focus-visible:outline-none focus-visible:ring-2"
                  />
                  <select
                    value={documentTypeFilter}
                    onChange={(e) => setDocumentTypeFilter(e.target.value)}
                    className="border-input bg-background h-9 min-w-[120px] rounded-md border px-2 text-sm"
                  >
                    <option value="all">
                      {t("profile.allTypes", "Tous les types")}
                    </option>
                    {documentTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                {filteredDocuments.map((doc: ProfileDocument) => {
                  const isEditing = editingDocId === doc.id;
                  const shownTitle = documentDisplayName(doc);

                  const commitLabel = async () => {
                    await updateLabelMutation.mutateAsync({
                      id: doc.id,
                      label: labelDraft.trim() || null,
                      portalClientId:
                        isAllProsMode && doc.clientId
                          ? doc.clientId
                          : undefined,
                    });
                    setEditingDocId(null);
                  };

                  return (
                    <div
                      key={doc.id}
                      className="hover:bg-muted/45 flex flex-wrap items-center gap-2 rounded-lg p-2 text-sm"
                    >
                      <FileText className="text-primary h-4 w-4 shrink-0" />
                      {isEditing ? (
                        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
                          <input
                            type="text"
                            value={labelDraft}
                            onChange={(e) => setLabelDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") void commitLabel();
                              if (e.key === "Escape") setEditingDocId(null);
                            }}
                            placeholder={doc.name}
                            className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring min-w-48 flex-1 rounded-md border px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2"
                            autoFocus
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-8 px-2"
                            disabled={updateLabelMutation.isPending}
                            onClick={() => void commitLabel()}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <span
                          className="min-w-0 flex-1 truncate font-medium"
                          title={shownTitle}
                        >
                          {shownTitle}
                        </span>
                      )}
                      <Badge variant="outline" className="text-[10px]">
                        {doc.type}
                      </Badge>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 shrink-0 p-0"
                        onClick={() => previewDocument(doc)}
                        aria-label={t("profile.previewDocument")}
                        title={t("profile.previewDocument")}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 shrink-0 p-0"
                        onClick={() => downloadDocument(doc)}
                        aria-label={t("profile.downloadDocument")}
                        title={t("profile.downloadDocument")}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      {!isEditing ? (
                        <>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="text-muted-foreground"
                            onClick={() => {
                              setEditingDocId(doc.id);
                              setLabelDraft(doc.label?.trim() ?? "");
                            }}
                            aria-label={t("profile.editDocumentLabel")}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="text-destructive"
                            disabled={deleteMutation.isPending}
                            onClick={() => setDeleteDocumentId(doc.id)}
                            aria-label={t("profile.deleteDocument")}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      ) : null}
                    </div>
                  );
                })}
                {filteredDocuments.length === 0 && (
                  <div className="text-muted-foreground flex flex-col items-center gap-2 py-4 text-center text-sm">
                    <EmptyIllustration size={120} className="text-muted-foreground/70" />
                    <p>
                      {t(
                        "profile.noDocumentsMatch",
                        "Aucun document ne correspond au filtre.",
                      )}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-muted-foreground flex flex-col items-center gap-3 py-6 text-center text-sm">
                <NoDataIllustration size={128} className="mx-auto" />
                <p>{t("profile.noDocuments")}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {hasIntakeData && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ClipboardList className="text-primary h-5 w-5" />
                <CardTitle>{t("profile.intakeData")}</CardTitle>
              </div>
              <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                <Lock className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">
                  {t("profile.intakeReadonly")}
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2">
              {intake!.intakeForm!.map((field) => {
                const value = intake!.clientData![field.id];
                if (!value) return null;

                if (field.type === "document") {
                  const docId =
                    typeof value === "string" ? value : String(value);
                  const doc = profile?.documents?.find(
                    (d: { id: string; name: string; label?: string | null }) =>
                      d.id === docId,
                  );
                  return (
                    <div key={field.id} className="space-y-1">
                      <Label className="text-muted-foreground text-xs">
                        {field.label}
                      </Label>
                      <p className="whitespace-pre-wrap text-sm font-medium">
                        {doc
                          ? documentDisplayName(doc)
                          : t("profile.documentProvided", "Document joint")}
                      </p>
                    </div>
                  );
                }

                return (
                  <div key={field.id} className="space-y-1">
                    <Label className="text-muted-foreground text-xs">
                      {field.label}
                    </Label>
                    <p className="whitespace-pre-wrap text-sm font-medium">
                      {value}
                    </p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-row items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Target className="text-primary h-5 w-5" />
              <CardTitle>{t("profile.objectives")}</CardTitle>
            </div>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/objectives">
                <ChevronRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {objectives && objectives.length > 0 ? (
            <div className="space-y-4">
              {objectives.map((obj) => (
                <div key={obj.id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={cn(
                          "font-medium",
                          obj.doneAt && "text-muted-foreground line-through",
                        )}
                      >
                        {obj.title}
                      </span>
                      {obj.doneAt && (
                        <Badge className="bg-green-600 text-[10px]">
                          {t("profile.done")}
                        </Badge>
                      )}
                    </div>
                    <span className="text-sm font-medium">{obj.progress}%</span>
                  </div>
                  {isAllProsMode && obj.client?.org?.name ? (
                    <p className="text-muted-foreground text-[11px]">
                      {obj.client.org.name}
                    </p>
                  ) : null}
                  <div className="bg-muted h-2 w-full rounded-full">
                    <div
                      className={cn(
                        "h-2 rounded-full transition-all",
                        obj.progress === 100
                          ? "bg-green-500"
                          : obj.progress >= 50
                            ? "bg-primary"
                            : "bg-orange-400",
                      )}
                      style={{ width: `${obj.progress}%` }}
                    />
                  </div>
                  {obj.description && (
                    <p className="text-muted-foreground text-xs">
                      {obj.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-muted-foreground flex flex-col items-center gap-2 py-6 text-center text-sm">
              <LottieIllustration
                animationData={emptyBoxLottie}
                size={150}
                className="opacity-90"
              />
              <p>{t("profile.noObjectives")}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={removePhotoDialogOpen} onOpenChange={setRemovePhotoDialogOpen}>
        <DialogContent
          aria-describedby="remove-photo-desc"
          className="sm:max-w-md sm:rounded-xl"
        >
          <DialogHeader>
            <DialogTitle>
              {t("profile.confirmRemovePhoto", "Supprimer votre photo de profil ?")}
            </DialogTitle>
            <DialogDescription id="remove-photo-desc">
              {t(
                "profile.removePhotoDialogDescription",
                "Votre photo sera retirée de votre profil.",
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setRemovePhotoDialogOpen(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={updatePhotoMutation.isPending}
              onClick={() => void confirmRemoveProfilePhoto()}
            >
              {updatePhotoMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              {t("profile.removePhoto")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!deleteDocumentId}
        onOpenChange={(v) => !v && setDeleteDocumentId(null)}
      >
        <DialogContent
          aria-describedby="delete-doc-desc"
          className="sm:max-w-md sm:rounded-xl"
        >
          <DialogHeader>
            <DialogTitle>{t("profile.deleteDocumentDialogTitle")}</DialogTitle>
            <DialogDescription id="delete-doc-desc">
              {t("profile.confirmDeleteDocument")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteDocumentId(null)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => {
                if (!deleteDocumentId) return;
                const docMeta = documents.find((d) => d.id === deleteDocumentId);
                void deleteMutation
                  .mutateAsync({
                    id: deleteDocumentId,
                    portalClientId:
                      isAllProsMode && docMeta?.clientId
                        ? docMeta.clientId
                        : undefined,
                  })
                  .then(() => {
                    setDeleteDocumentId(null);
                  });
              }}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              {t("common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
